import { useState, useEffect, useMemo } from 'react'
import './App.css'

interface Item {
  id: number;
  name: string;
  description: string;
  image_url?: string;
  user_id?: number;
  user_email?: string;
  user_name?: string;
  created_at: string;
}

interface User {
  id: number;
  email: string;
  name: string | null;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

function App() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', description: '' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });

  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [refreshToken, setRefreshToken] = useState<string>('');
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    name: '',
  });

  const isAuthenticated = useMemo(() => !!accessToken && !!user, [accessToken, user]);

  // Load tokens from localStorage on mount
  useEffect(() => {
    const savedAccessToken = localStorage.getItem('accessToken');
    const savedRefreshToken = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');

    if (savedAccessToken && savedRefreshToken && savedUser) {
      setAccessToken(savedAccessToken);
      setRefreshToken(savedRefreshToken);
      setUser(JSON.parse(savedUser));

      // Fetch initial data with saved token
      fetchItems();
    }
  }, []);

  // Auth functions
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const body: any = {
        email: authForm.email,
        password: authForm.password,
      };

      if (authMode === 'register' && authForm.name) {
        body.name = authForm.name;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const text = await response.text();
      let data: any;

      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error('Server returned invalid response');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      // If register, show success message and switch to login
      if (authMode === 'register') {
        setSuccessMessage('✅ Registration successful! Please login with your credentials.');
        setAuthMode('login');
        setAuthForm({ email: authForm.email, password: '', name: '' });
        return;
      }

      // If login, save tokens and user
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setUser(data.user);

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Reset form
      setAuthForm({ email: '', password: '', name: '' });

      // Fetch initial data
      await fetchItems();
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    // Clear local state
    setAccessToken('');
    setRefreshToken('');
    setUser(null);
    setItems([]);

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };

  const refreshAccessToken = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      setAccessToken(data.accessToken);
      localStorage.setItem('accessToken', data.accessToken);

      return data.accessToken;
    } catch (err) {
      console.error('Token refresh error:', err);
      // Refresh failed, logout user
      await handleLogout();
      throw err;
    }
  };

  const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
    // Get token from localStorage if not in state yet (e.g., after page refresh)
    const token = accessToken || localStorage.getItem('accessToken') || '';

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    let response = await fetch(url, { ...options, headers });

    // If 401, try to refresh token and retry
    if (response.status === 401) {
      await refreshAccessToken();

      // Get updated token
      const newToken = localStorage.getItem('accessToken') || '';
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    }

    return response;
  };

  const fetchItems = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/items`);
      const data = await response.json();
      setItems(data.items || []);
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const handleCreateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItem.name) return;

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', newItem.name);
      formData.append('description', newItem.description);
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await fetchWithAuth(`${API_URL}/items`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create item');
      }

      setNewItem({ name: '', description: '' });
      setImageFile(null);
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      await fetchItems();
    } catch (err: any) {
      console.error('Error creating item:', err);
      setError(err.message || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/items/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete item');

      await fetchItems();
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete item');
    }
  };

  const handleEditItem = (item: Item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, description: item.description });
  };

  const handleUpdateItem = async (id: number) => {
    if (!editForm.name) return;

    try {
      const response = await fetchWithAuth(`${API_URL}/items/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });

      if (!response.ok) throw new Error('Failed to update item');

      setEditingId(null);
      await fetchItems();
      setSuccessMessage('Item updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error updating item:', err);
      setError(err.message || 'Failed to update item');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', description: '' });
  };

  return (
    <div className="app">
      {/* Header with Auth Status */}
      <div className="header">
        <div>
          <h1>☁️ Cloudflare Stack Demo</h1>
          <p className="subtitle">Frontend: React + Vite | Backend: Workers + Hono + JWT</p>
        </div>
        {user && (
          <div className="user-info">
            <span>👤 {user.email}</span>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {successMessage && <div className="success">{successMessage}</div>}

      {/* Login/Register Form (shown when not authenticated) */}
      {!isAuthenticated ? (
        <div className="auth-section">
          <div className="auth-tabs">
            <button
              className={authMode === 'login' ? 'active' : ''}
              onClick={() => setAuthMode('login')}
            >
              Login
            </button>
            <button
              className={authMode === 'register' ? 'active' : ''}
              onClick={() => setAuthMode('register')}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleAuth} className="auth-form">
            <input
              type="email"
              placeholder="Email"
              value={authForm.email}
              onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
              required
            />
            <input
              type="password"
              placeholder="Password (min 6 characters)"
              value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              required
              minLength={6}
            />
            {authMode === 'register' && (
              <input
                type="text"
                placeholder="Name"
                value={authForm.name}
                onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                required
              />
            )}
            <button type="submit" disabled={loading}>
              {loading ? 'Processing...' : authMode === 'login' ? 'Login' : 'Register'}
            </button>
          </form>
        </div>
      ) : (
        // Main Content (shown when authenticated)
        <div className="main-content">
        <section className="section">
          <h2>📋 Items (D1 Database + R2 Images)</h2>

          <form onSubmit={handleCreateItem} className="form">
            <input
              type="text"
              placeholder="Item name"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              required
            />
            <textarea
              placeholder="Description (optional)"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              rows={3}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
            {imageFile && <small className="file-hint">📷 {imageFile.name}</small>}
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Item'}
            </button>
          </form>

          <div className="items-list">
            {items.length === 0 ? (
              <p className="empty">No items yet. Create one above!</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className="item-card">
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="item-image"
                    />
                  )}
                  {editingId === item.id ? (
                    <div className="edit-form">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        placeholder="Name"
                        required
                      />
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        placeholder="Description"
                        rows={3}
                      />
                      <div className="edit-actions">
                        <button onClick={() => handleUpdateItem(item.id)} className="save-btn">✓ Save</button>
                        <button onClick={handleCancelEdit} className="cancel-btn">✕ Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3>{item.name}</h3>
                      <p>{item.description}</p>
                      <small>
                        Posted by: {item.user_name || item.user_email || 'Unknown'} • {new Date(item.created_at).toLocaleString()}
                      </small>
                    </div>
                  )}
                  {user && item.user_id === user.id && editingId !== item.id && (
                    <div className="item-actions">
                      <button
                        onClick={() => handleEditItem(item)}
                        className="edit-btn"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="delete-btn"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      </div>
      )}
    </div>
  );
}

export default App;
